import { Alert, Spin, Typography } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import TestProjectBaselineImpactList from '../TestProjectBaselineImpactList';
import {
    TestProjectTopicsTypeSelector,
    TestProjectTopicsAppSelector,
    TestProjectTopicsMappingPanel,
    TestProjectTopicsAppInputGroupSelector
} from '../TestProjectTopicsWizard';
import '../TestProjectTopicsWizard/TestProjectTopicsWizard.less';

import type { TestProjectInputMappingDraft } from '../../models/TestProjectInputEditorDraft';
import type { TestProjectInputDialogProps } from './types';

type Props = TestProjectInputDialogProps & { draft: TestProjectInputMappingDraft; confirmation?: boolean };

export const TestProjectInputMappingContent = observer(({ store, draft, confirmation }: Props) => {
    const impact = draft.prepared?.impact || draft.baselineImpact;
    const retained = impact?.retained.reduce((count, input) => count + input.baselineCount, 0) || 0;
    const removed = impact?.removed.reduce((count, input) => count + input.baselineCount, 0) || 0;
    const hasTargets = draft.mappings.some(mapping => mapping.newValue);

    const getImpactSummary = () => {
        if (!impact) {
            return null;
        }

        if (!hasTargets) {
            return (
                <Typography.Paragraph className="test-project-mapping-impact">
                    Choose a target input to see what will happen to the baselines.
                </Typography.Paragraph>
            );
        }

        return (
            <Typography.Paragraph className="test-project-mapping-impact">
                {retained} {retained === 1 ? 'baseline' : 'baselines'} will be retained, {removed}{' '}
                {removed === 1 ? 'baseline' : 'baselines'} will be deleted.
            </Typography.Paragraph>
        );
    };

    return (
        <>
            {confirmation ? (
                <>
                    <Alert
                        type="warning"
                        showIcon
                        message={
                            <>
                                Inputs without a mapping and their baselines will be{' '}
                                <strong>permanently deleted</strong>.
                            </>
                        }
                        style={{ marginBottom: 16 }}
                    />
                    <TestProjectBaselineImpactList inputs={impact?.removed || []} />
                </>
            ) : (
                <Spin
                    wrapperClassName="test-project-input-mapping"
                    spinning={draft.isLoadingBaselines || draft.inputSelectionStore.isLoadingMetadata}
                >
                    <Typography.Paragraph>
                        {draft.mappingTargetInputType ? (
                            <>
                                Choose the binding that should replace each tag input.{' '}
                                <strong>Existing baselines will be kept</strong> for mapped inputs.
                            </>
                        ) : (
                            'Mapped inputs keep their baseline values and fuzzy thresholds.'
                        )}
                    </Typography.Paragraph>
                    {getImpactSummary()}
                    <div className="topics-wizard-type-selection-wrapper">
                        <TestProjectTopicsTypeSelector store={store} />
                        <TestProjectTopicsAppSelector store={store} />
                        <TestProjectTopicsAppInputGroupSelector store={store} />
                    </div>
                    <div className="topic-wizard-mapping-panel-wrapper">
                        <TestProjectTopicsMappingPanel store={store} />
                    </div>
                </Spin>
            )}
        </>
    );
});
